import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, getSupabase, isSupabaseConfigured } from '../lib/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface SupabaseContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isConfigured: boolean;
}

const SupabaseContext = createContext<SupabaseContextValue>({
  user: null,
  session: null,
  isLoading: true,
  isConfigured: false,
});

export const useSupabaseContext = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabaseContext must be used within SupabaseProvider');
  }
  return context;
};

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export const SupabaseProvider: React.FC<SupabaseProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    // Check if Supabase is configured
    setIsConfigured(isSupabaseConfigured());

    // Get initial session
    const initializeSession = async () => {
      try {
        const client = getSupabase();
        const { data: { session: currentSession }, error } = await client.auth.getSession();

        if (error) {
          console.error('[SupabaseProvider] Error getting session:', error);
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);
      } catch (error) {
        console.error('[SupabaseProvider] Failed to initialize session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('[SupabaseProvider] Auth state changed:', event);

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Handle specific events
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('[SupabaseProvider] Token refreshed successfully');
        } else if (event === 'SIGNED_IN') {
          console.log('[SupabaseProvider] User signed in');
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Handle HMR updates
  useEffect(() => {
    if (import.meta.hot) {
      import.meta.hot.on('vite:beforeUpdate', () => {
        console.log('[SupabaseProvider] HMR update detected, preserving auth state');
      });
    }
  }, []);

  const value: SupabaseContextValue = {
    user,
    session,
    isLoading,
    isConfigured,
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};
